<template>
  <article
    class="ac-card"
    :class="`ac-card--${item.tone}`"
    :data-test="item.testId"
    :data-alert-key="item.key"
  >
    <header class="ac-card-top">
      <span class="ac-badge" :class="`ac-badge--${item.tone}`">{{ item.label }}</span>
      <time class="ac-card-age" :datetime="isoTime" :title="item.time">{{ item.age }}</time>
    </header>

    <div class="ac-card-who">
      <div class="ac-card-name">{{ item.title }}</div>
      <div v-if="item.subtitle" class="ac-card-sub">{{ item.subtitle }}</div>
    </div>

    <blockquote v-if="item.quote" class="ac-card-quote">“{{ item.quote }}”</blockquote>

    <dl v-if="item.meta && item.meta.length" class="ac-card-meta">
      <div v-for="row in item.meta" :key="row.k" class="ac-card-meta-row">
        <dt>{{ row.k }}</dt>
        <dd>{{ row.v }}</dd>
      </div>
    </dl>

    <ol v-if="item.steps && item.steps.length" class="ac-card-steps" data-test="ac-card-steps">
      <li v-for="s in item.steps" :key="s.step">
        <button
          type="button"
          class="ac-step"
          :disabled="!s.to"
          :data-test="`ac-card-step-${s.step}`"
          @click="$emit('open-step', s.to)"
        >
          <span class="ac-step-num">{{ s.step }}</span>
          <span class="ac-step-name">{{ s.formName }}</span>
          <span class="ac-step-time">{{ s.time }}</span>
        </button>
      </li>
    </ol>

    <p v-if="item.note" class="ac-card-note">{{ item.note }}</p>

    <form v-if="item.reply" class="ac-reply" @submit.prevent="onSend">
      <input
        class="ac-reply-input"
        type="text"
        :value="replyText"
        :placeholder="replySending ? 'Sending…' : replyPlaceholder"
        :aria-label="`Reply to ${item.title}`"
        :disabled="replySending"
        data-test="ac-card-reply-input"
        @input="$emit('reply-input', $event.target.value)"
        @focus="$emit('reply-focus')"
        @blur="$emit('reply-blur')"
      />
      <button
        type="submit"
        class="ac-reply-send"
        :disabled="!canSend"
        aria-label="Send reply"
        data-test="ac-card-reply-send"
      >
        <AcIcon name="send-horizontal" :size="16" />
      </button>
    </form>

    <div class="ac-card-actions">
      <button
        v-if="item.primary"
        type="button"
        class="ac-btn ac-btn--secondary"
        :disabled="busy"
        data-test="ac-card-primary"
        @click="$emit('primary', item)"
      >{{ item.primary.label }}</button>
      <button
        type="button"
        class="ac-btn ac-btn--ghost"
        :disabled="busy"
        data-test="ac-card-dismiss"
        @click="$emit('dismiss', item)"
      >{{ item.secondary.label }}</button>
    </div>
  </article>
</template>

<script>
import AcIcon from './AcIcon.vue';

// One Action Center alert. `item` is a view model from src/utils/actionCenterAlerts.js — this component
// only renders it and emits intents; the panel owns Firestore writes, sends and navigation.
export default {
  name: 'AlertCard',
  components: { AcIcon },
  props: {
    item: { type: Object, required: true },
    replyText: { type: String, default: '' },
    replySending: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  computed: {
    canSend() {
      return !this.replySending && this.replyText.trim().length > 0;
    },
    replyPlaceholder() {
      return this.item.source === 'sms' ? 'Quick reply…' : 'Reply to the patient…';
    },
    isoTime() {
      const ms = Number(this.item.displayAt);
      return Number.isFinite(ms) && ms > 0 ? new Date(ms).toISOString() : null;
    },
  },
  methods: {
    onSend() {
      if (this.canSend) this.$emit('reply-send');
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-card {
  background: var(--ac-surface);
  border: 1px solid var(--ac-border);
  border-left: 3px solid var(--ac-card-accent, var(--ac-border-strong));
  border-radius: 12px;
  padding: 13px 14px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  box-shadow: var(--ac-shadow-xs);
  color: var(--ac-text);
  min-width: 0;
}

.ac-card--red { --ac-card-accent: var(--ac-red-500); }
.ac-card--orange { --ac-card-accent: var(--ac-orange-500); }
.ac-card--blue { --ac-card-accent: var(--ac-blue-500); }
.ac-card--iris { --ac-card-accent: var(--ac-iris-600); }
.ac-card--purple { --ac-card-accent: var(--ac-purple-500); }
.ac-card--amber { --ac-card-accent: var(--ac-amber-500); }
.ac-card--green { --ac-card-accent: var(--ac-green-600); }

.ac-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ac-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 7px;
  border-radius: 999px;
  border: 1px solid transparent;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.ac-badge--red { color: #c2333a; background: var(--ac-red-50); border-color: #f8d4d5; }
.ac-badge--orange { color: #b54708; background: #fff4e5; border-color: #fde2c2; }
.ac-badge--blue { color: #1d4ed8; background: var(--ac-blue-50); border-color: #dbe7ff; }
.ac-badge--iris { color: #fff; background: var(--ac-iris-600); }
.ac-badge--purple { color: #6d28d9; background: var(--ac-purple-50); border-color: #e9defc; }
.ac-badge--amber { color: #b45309; background: var(--ac-amber-50); border-color: #fde9c8; }
.ac-badge--green { color: var(--ac-green-600); background: var(--ac-green-50); border-color: #c6ebdc; }

.ac-card-age {
  font-size: 11px;
  font-weight: 500;
  color: var(--ac-text-subtle);
  white-space: nowrap;
}

.ac-card-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.ac-card-sub {
  font-size: 13px;
  color: var(--ac-text-muted);
  margin-top: 2px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.ac-card-quote {
  margin: 0;
  border-left: 2px solid var(--ac-iris-200);
  padding-left: 10px;
  font-size: 13px;
  font-style: italic;
  color: var(--ac-neutral-700);
  line-height: 1.45;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ac-card-meta {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.ac-card-meta-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;

  dt {
    font-weight: 400;
    color: var(--ac-text-muted);
    white-space: nowrap;
  }

  dd {
    margin: 0;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: right;
    overflow-wrap: anywhere;
  }
}

.ac-card-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ac-border);
  border-radius: var(--ac-radius-md);
  overflow: hidden;

  li + li {
    border-top: 1px solid var(--ac-border);
  }
}

.ac-step {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: 0;
  background: var(--ac-surface);
  font: inherit;
  font-size: 13px;
  color: var(--ac-text);
  text-align: left;
  cursor: pointer;

  &:hover:not([disabled]) {
    background: var(--ac-iris-50);
  }

  &:focus-visible {
    outline: 2px solid var(--ac-iris-300);
    outline-offset: -2px;
  }

  &[disabled] {
    cursor: default;
    color: var(--ac-text-muted);
  }
}

.ac-step-num {
  width: 20px;
  height: 20px;
  flex: none;
  border-radius: 50%;
  background: var(--ac-iris-50);
  color: var(--ac-iris-700);
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ac-step-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ac-step-time {
  flex: none;
  font-size: 11px;
  color: var(--ac-text-subtle);
}

.ac-card-note {
  margin: 0;
  font-size: 12px;
  color: var(--ac-iris-800);
  background: var(--ac-iris-50);
  border-radius: var(--ac-radius-sm);
  padding: 6px 8px;
  line-height: 1.4;
}

.ac-reply {
  display: flex;
  gap: 6px;
  margin: 0;
}

.ac-reply-input {
  flex: 1;
  min-width: 0;
  height: 36px;
  border: 1px solid var(--ac-border);
  border-radius: var(--ac-radius-md);
  padding: 0 10px;
  font: inherit;
  font-size: 13px;
  color: var(--ac-text);
  background: var(--ac-neutral-25);
  outline: none;

  &:focus {
    border-color: var(--ac-iris-600);
    background: var(--ac-surface);
    box-shadow: 0 0 0 3px rgba(91, 91, 214, 0.15);
  }

  &:disabled {
    opacity: 0.6;
  }
}

.ac-reply-send {
  width: 36px;
  height: 36px;
  flex: none;
  border: 0;
  border-radius: var(--ac-radius-md);
  background: var(--ac-iris-600);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not([disabled]) {
    background: var(--ac-iris-700);
  }

  &[disabled] {
    opacity: 0.4;
    cursor: default;
  }
}

.ac-card-actions {
  display: flex;
  gap: 6px;
}

.ac-btn {
  height: 34px;
  padding: 0 14px;
  border-radius: var(--ac-radius-md);
  border: 1px solid transparent;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(91, 91, 214, 0.3);
  }

  &[disabled] {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ac-btn--secondary {
  background: var(--ac-surface);
  border-color: var(--ac-border-strong);
  color: var(--ac-text);

  &:hover:not([disabled]) {
    background: var(--ac-neutral-50);
  }
}

.ac-btn--ghost {
  background: transparent;
  color: var(--ac-neutral-700);

  &:hover:not([disabled]) {
    background: var(--ac-neutral-100);
  }
}

// Phones: 44px touch targets, buttons full width so they are thumb-reachable.
@media (max-width: 767.98px) {
  .ac-reply-input,
  .ac-reply-send {
    height: 44px;
  }

  .ac-reply-send {
    width: 44px;
  }

  // 16px keeps iOS Safari from zooming into the field on focus.
  .ac-reply-input {
    font-size: 16px;
  }

  .ac-btn {
    height: 44px;
  }

  .ac-btn--secondary {
    flex: 1;
  }

  .ac-btn--ghost {
    flex: none;
    padding: 0 16px;
    background: var(--ac-neutral-100);
  }

  .ac-step {
    min-height: 44px;
  }
}
</style>
